import {
  Banner,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  RadioGroup,
  Tooltip,
  Typography,
} from '@forgeglobal/design-framework';
import { AccountModel } from '@tcs/client-portal-api/platform_generated';
import { Account } from '@tcs/client-portal-api/types/Account';
import ProviderAutocomplete from '@tcs/client-portal-common/components/atoms/ProviderAutocomplete';
import { Provider } from '@tcs/client-portal-common/hooks/useProviderLookup';
import { STATES } from '@tcs/common/assets/constants';
import Button from '@tcs/common/components/atoms/Button';
import DropdownField from '@tcs/common/components/atoms/DropdownField';
import Form, {
  Field,
  FormRenderProps,
} from '@tcs/common/components/atoms/Form';
import PhoneNumberTextField from '@tcs/common/components/atoms/PhoneNumberTextField';
import Radio from '@tcs/common/components/atoms/Radio';
import TextField from '@tcs/common/components/atoms/TextField';
import ZipCodeTextField from '@tcs/common/components/atoms/ZipCodeTextField';
import { CashDeliveryMethod } from '@tcs/common/types/CashDeliveryMethod';
import { TransferMethod } from '@tcs/common/types/TransferMethod';
import { TransferType } from '@tcs/common/types/TransferType';
import { Unicode } from '@tcs/common/utils/unicode';
import { first } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useStyles } from '../style';
import { TransfersInCashFormSchema } from './TransfersInCashValidators';

export const ACCOUNT_TYPE_MAP = {
  [AccountModel.AccountType.TRADITIONAL_IRA]: 'Traditional',
  [AccountModel.AccountType.ROTH_IRA]: 'Roth',
  [AccountModel.AccountType.SEP_IRA]: 'SEP',
  [AccountModel.AccountType.SIMPLE_IRA]: 'Simple',
  [AccountModel.AccountType.COVERDELL_EDUCATION_SAVINGS]: 'CESA',
  [AccountModel.AccountType.SOLO_TRADITIONAL401K]: 'Solo',
};

export const TRANSFER_ACCOUNT_TYPE_OPTIONS = [
  {
    label: 'Traditional IRA',
    value: ACCOUNT_TYPE_MAP.TraditionalIra,
  },
  { label: 'Roth IRA', value: ACCOUNT_TYPE_MAP.RothIra },
  { label: 'SEP IRA', value: ACCOUNT_TYPE_MAP.SepIra },
  { label: 'SIMPLE IRA', value: ACCOUNT_TYPE_MAP.SimpleIra },
];

export interface TransfersInCashFormData {
  id?: string;
  selectedAccount: string;
  transferFromAccount: string;
  accountType: AccountModel.AccountType;
  isInheritedAccount: boolean;
  iraProvider: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  transferType: TransferType;
  transferMethod: TransferMethod;
  transferAmount?: number;
  expediteProcessing: boolean;
  overnightDelivery: boolean;
  overnightDeliveryCostFedexAccountNumber?: string;
  deliverCashBy: CashDeliveryMethod;
}

interface TransfersInCashFormProps {
  accounts: Account[];
  transferData?: Partial<TransfersInCashFormData>;
  onCancel: () => void;
  onSubmit: (data: TransfersInCashFormData) => void;
}

const DEFAULT_FORM_VALUES: Partial<TransfersInCashFormData> = {
  transferType: TransferType.Partial,
  transferMethod: TransferMethod.ExactAmount,
  isInheritedAccount: false,
  expediteProcessing: false,
  overnightDelivery: false,
  overnightDeliveryCostFedexAccountNumber: '',
  deliverCashBy: CashDeliveryMethod.Wire,
};

const FEE_DISCLOSURE_URL =
  'https://forgetrust.com/forms/fee-schedule-and-financial-disclosure/';

export const TransfersInCashForm = ({
  accounts = [],
  onCancel,
  handleSubmit,
  submitting,
  values,
  form,
}: TransfersInCashFormProps & FormRenderProps) => {
  const classes = useStyles();

  const dropdownSx = {
    '& .MuiFilledInput-root': {
      background: '#FFF !important',
      '&.Mui-error': {
        backgroundColor: '#FCFAFA !important',
      },
      '& .MuiSelect-select:focus': {
        backgroundColor: '#FFF !important',
      },
      '&:hover': {
        backgroundColor: '#FFF !important',
      },
    },
  };

  const inputSx = {
    '& .MuiFilledInput-root': {
      background: '#FFF',
      '&.Mui-focused': {
        backgroundColor: '#FFF !important',
      },
      '&:hover': {
        backgroundColor: '#FFF !important',
      },
    },
  };

  const accountOptions = [
    { label: 'Select an account', value: '' },
    ...accounts.map((account) => ({
      label: `${account.displayAccountType} - ${account.accountNumber}`,
      value: account.accountNumber,
    })),
  ];

  const handleProviderSelect = (provider: Provider) => {
    // Auto-populate address fields when a provider is selected
    form.change('address', provider.address);
    form.change('city', provider.city);
    form.change('state', provider.state);
    form.change('zipCode', provider.zipCode);
    form.change('phone', provider.phone);
  };

  const handleProviderClear = () => {
    // Clear address and contact fields when provider is cleared
    form.change('address', '');
    form.change('city', '');
    form.change('state', '');
    form.change('zipCode', '');
    form.change('phone', '');
  };

  const selectedAccountObj = useMemo(
    () => accounts.find((acc) => acc.accountNumber === values.selectedAccount),
    [accounts, values.selectedAccount],
  );

  const isInheritedToAccount = selectedAccountObj?.isInherited ?? false;
  const isRothOrTraditionalIraAccount =
    isInheritedToAccount &&
    (selectedAccountObj?.actualAccountType ===
      AccountModel.AccountType.ROTH_IRA ||
      selectedAccountObj?.actualAccountType ===
        AccountModel.AccountType.TRADITIONAL_IRA);
  const isInheritedTraditional =
    isInheritedToAccount &&
    selectedAccountObj?.actualAccountType ===
      AccountModel.AccountType.TRADITIONAL_IRA;
  const isInheritedRoth =
    isInheritedToAccount &&
    selectedAccountObj?.actualAccountType === AccountModel.AccountType.ROTH_IRA;

  const accountTypes = useMemo(() => {
    if (isInheritedTraditional) {
      return [
        {
          label: 'Inherited Traditional IRA',
          value: ACCOUNT_TYPE_MAP.TraditionalIra,
        },
      ];
    }
    if (isInheritedRoth) {
      return [
        {
          label: 'Inherited Roth IRA',
          value: ACCOUNT_TYPE_MAP.RothIra,
        },
      ];
    }
    const filteredAccountTypes = !isRothOrTraditionalIraAccount
      ? TRANSFER_ACCOUNT_TYPE_OPTIONS
      : TRANSFER_ACCOUNT_TYPE_OPTIONS.filter(
          (option) =>
            option.value !== ACCOUNT_TYPE_MAP.RothIra &&
            option.value !== ACCOUNT_TYPE_MAP.TraditionalIra,
        );
    return [{ label: Unicode.EmDash, value: '' }, ...filteredAccountTypes];
  }, [isRothOrTraditionalIraAccount, isInheritedTraditional, isInheritedRoth]);

  useEffect(() => {
    if (isInheritedTraditional) {
      form.change('accountType', ACCOUNT_TYPE_MAP.TraditionalIra);
    } else if (isInheritedRoth) {
      form.change('accountType', ACCOUNT_TYPE_MAP.RothIra);
    }
  }, [
    isRothOrTraditionalIraAccount,
    isInheritedTraditional,
    isInheritedRoth,
    form,
    values.accountType,
  ]);

  useEffect(() => {
    form.change('isInheritedAccount', isInheritedToAccount);
  }, [isInheritedToAccount, form]);

  const renderAccountsDropdown = () => {
    if (accounts.length === 1) {
      return null;
    }

    return (
      <>
        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Transferring to
          </Typography>
          <Field
            name="selectedAccount"
            render={(props) => (
              <DropdownField
                label="Select an account"
                {...props}
                options={accountOptions}
                fullWidth
                sx={dropdownSx}
                data-test-id="selected-account"
              />
            )}
          />
        </Box>
      </>
    );
  };

  return (
    <>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
        Transfer funds
      </Typography>

      <Banner severity="info" classes={{ root: classes.banner }}>
        <Typography variant="body2">
          Please refer to a statement from your current company to help complete
          this process. You will also need to provide a recent statement to
          complete this transfer request.
        </Typography>
      </Banner>

      <form onSubmit={handleSubmit} noValidate>
        {renderAccountsDropdown()}

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Transferring from
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Field
                name="transferFromAccount"
                render={(props) => (
                  <TextField
                    {...props}
                    label="Account number"
                    fullWidth
                    sx={inputSx}
                    data-test-id="transfer-from-account"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="accountType"
                render={(props) => (
                  <DropdownField
                    {...props}
                    label="Account type"
                    options={accountTypes}
                    fullWidth
                    sx={dropdownSx}
                    data-test-id="account-type"
                    disabled={isRothOrTraditionalIraAccount}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
            Account numbers may contain letters as well as numbers.
          </Typography>

          <Box sx={{ pb: 2 }}>
            <Field
              name="iraProvider"
              render={(props) => (
                <ProviderAutocomplete
                  {...props}
                  label="Your current IRA provider"
                  fullWidth
                  sx={inputSx}
                  onProviderSelect={handleProviderSelect}
                  onProviderClear={handleProviderClear}
                  data-test-id="ira-provider"
                />
              )}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Field
                name="address"
                render={(props) => (
                  <TextField
                    {...props}
                    label="Street address"
                    fullWidth
                    sx={inputSx}
                    data-test-id="address"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="city"
                render={(props) => (
                  <TextField
                    {...props}
                    label="City"
                    fullWidth
                    sx={inputSx}
                    data-test-id="city"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="state"
                render={(props) => (
                  <DropdownField
                    {...props}
                    label="State"
                    options={[
                      { label: Unicode.EmDash, value: '' },
                      ...STATES.map(({ Id, Name }) => ({
                        label: Name,
                        value: Id,
                      })),
                    ]}
                    fullWidth
                    sx={dropdownSx}
                    data-test-id="state"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="zipCode"
                render={(props) => (
                  <ZipCodeTextField
                    {...props}
                    label="Zip code"
                    fullWidth
                    sx={inputSx}
                    data-test-id="zipcode"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                name="phone"
                render={(props) => (
                  <PhoneNumberTextField
                    {...props}
                    label="Phone number"
                    fullWidth
                    sx={inputSx}
                    data-test-id="phone"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Cash to transfer from
          </Typography>

          <Typography variant="body2" className={classes.preferencesTitle}>
            Transfer type
          </Typography>
          <RadioGroup sx={{ mb: 3 }}>
            <Field
              name="transferType"
              value={TransferType.Full}
              type="radio"
              render={(props: any) => (
                <FormControlLabel
                  value={TransferType.Full}
                  control={
                    <Radio
                      {...props}
                      input={{
                        ...props.input,
                        onChange: (e) => {
                          props.input.onChange(e);
                          form.change(
                            'transferMethod',
                            TransferMethod.AllAvailableCash,
                          );
                        },
                      }}
                    />
                  }
                  label="Full (This will close my account)"
                />
              )}
            />
            <Field
              name="transferType"
              value={TransferType.Partial}
              type="radio"
              render={(props: any) => (
                <FormControlLabel
                  value={TransferType.Partial}
                  control={
                    <Radio
                      {...props}
                      input={{
                        ...props.input,
                        onChange: (e) => {
                          props.input.onChange(e);
                          form.change(
                            'transferMethod',
                            TransferMethod.ExactAmount,
                          );
                        },
                      }}
                    />
                  }
                  label="Partial (This account will remain open)"
                />
              )}
            />
          </RadioGroup>

          <Typography variant="body2" className={classes.preferencesTitle}>
            Transfer
          </Typography>
          <RadioGroup sx={{ mb: 2 }}>
            <Field
              name="transferMethod"
              value={TransferMethod.AllAvailableCash}
              type="radio"
              render={(props: any) => (
                <FormControlLabel
                  value={TransferMethod.AllAvailableCash}
                  control={<Radio {...props} />}
                  label="All available cash"
                  disabled={values.transferType === TransferType.Partial}
                />
              )}
            />
            <Field
              name="transferMethod"
              value={TransferMethod.ExactAmount}
              type="radio"
              render={(props: any) => (
                <FormControlLabel
                  value={TransferMethod.ExactAmount}
                  control={<Radio {...props} />}
                  label="Exact amount"
                  disabled={values.transferType === TransferType.Full}
                />
              )}
            />
          </RadioGroup>

          {values.transferMethod === TransferMethod.ExactAmount && (
            <Field
              name="transferAmount"
              render={(props) => (
                <TextField
                  {...props}
                  label="Exact cash amount ($)"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={inputSx}
                  data-test-id="transfer-amount"
                />
              )}
            />
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 10 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
            Delivery preferences
          </Typography>

          <Typography variant="body1" className={classes.preferencesTitle}>
            Expedite processing
          </Typography>
          <Field
            name="expediteProcessing"
            type="checkbox"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.input.checked}
                    onChange={props.input.onChange}
                    sx={{ mr: 1, color: '#05A0D9 !important' }}
                    data-test-id="expedite-processing"
                  />
                }
                label={
                  <p>
                    Check this box if you wish to expedite processing (
                    <Link
                      href={FEE_DISCLOSURE_URL}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ mt: 0 }}>
                      fees may apply
                    </Link>
                    )
                  </p>
                }
                sx={{ mb: 3, ml: 0 }}
              />
            )}
          />

          <Typography variant="body1" className={classes.preferencesTitle}>
            Overnight delivery to from custodian
          </Typography>
          <Field
            name="overnightDelivery"
            type="checkbox"
            render={(props) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={props.input.checked}
                    onChange={(e) => {
                      props.input.onChange(e);
                      if (!e.target.checked) {
                        form.change(
                          'overnightDeliveryCostFedexAccountNumber',
                          '',
                        );
                      }
                    }}
                    sx={{ mr: 1, color: '#05A0D9 !important' }}
                    data-test-id="overnight-delivery"
                  />
                }
                label={
                  <p>
                    Check this box to request overnight delivery to From
                    Custodian (
                    <Link
                      href={FEE_DISCLOSURE_URL}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ mt: 0 }}>
                      fees may apply
                    </Link>
                    )
                  </p>
                }
                sx={{ mb: 3, ml: 0 }}
              />
            )}
          />

          {values.overnightDelivery && (
            <Field
              name="overnightDeliveryCostFedexAccountNumber"
              render={(props) => (
                <TextField
                  {...props}
                  label="FedEx Account Number"
                  fullWidth
                  inputProps={{ maxLength: 16 }}
                  sx={{ mb: 3 }}
                  data-test-id="overnight-delivery-fedex-account-number"
                />
              )}
            />
          )}

          <Typography variant="body1" className={classes.preferencesTitle}>
            Deliver cash to Forge Trust by
          </Typography>
          <RadioGroup sx={{ mb: 3 }}>
            <Field
              name="deliverCashBy"
              value={CashDeliveryMethod.Check}
              type="radio"
              render={(props: any) => (
                <Tooltip
                  title="Check (5-day hold required)"
                  placement="left-start"
                  arrow>
                  <FormControlLabel
                    value={CashDeliveryMethod.Check}
                    control={<Radio {...props} />}
                    label="Check"
                  />
                </Tooltip>
              )}
            />
            <Field
              name="deliverCashBy"
              value={CashDeliveryMethod.Wire}
              type="radio"
              render={(props: any) => (
                <FormControlLabel
                  value={CashDeliveryMethod.Wire}
                  control={<Radio {...props} />}
                  label={
                    <p>
                      Wire (
                      <Link
                        href={FEE_DISCLOSURE_URL}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ mt: 0 }}>
                        fees may apply
                      </Link>
                      )
                    </p>
                  }
                />
              )}
            />
          </RadioGroup>
        </Box>

        <div className={classes.formActions}>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={submitting}
            tracking={{ action: 'TransfersInCashCanceled' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={submitting}
            tracking={{ action: 'TransfersInCashFormSubmitted' }}>
            Continue
          </Button>
        </div>
      </form>
    </>
  );
};

const TransfersInCashFormWrapper = ({
  accounts = [],
  transferData = DEFAULT_FORM_VALUES,
  onCancel,
  onSubmit,
}: TransfersInCashFormProps) => {
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>();

  useEffect(() => {
    if (accounts.length === 1) {
      const { accountNumber } = first(accounts);
      setSelectedAccount(accountNumber);
    }
  }, [accounts]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        selectedAccount,
        ...DEFAULT_FORM_VALUES,
        ...transferData,
      }}
      validationSchema={TransfersInCashFormSchema()}
      render={(formProps: FormRenderProps) => (
        <TransfersInCashForm
          accounts={accounts}
          onCancel={onCancel}
          onSubmit={onSubmit}
          {...formProps}
        />
      )}
    />
  );
};

export default TransfersInCashFormWrapper;
